"use client";

import { cn } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@app/_components/Accordion/Accordion";
import Link from "@app/_components/Link";
import StockIndicator from "@app/_components/Stock/statuses/StockIndicator";
import { StockStatusEnum } from "@app/_components/Stock/Stock.types";
import { Heading } from "@app/_components/ui";
import { useMarket } from "@app/_context";
import useTranslate from "@app/_hooks/useTranslate";
import { StoreFragment } from "@server/gql/graphql";
import type { ComponentProps } from "react";
import { mapOpeningHours } from "./mapOpeningHours";
// import { useStoreLocation } from "./StoreLocator.store";

export type StoreWithStock = StoreFragment & {
  store?: number;
  display?: number;
  displaySiblings?: number;
  storeSiblings?: number;
};

function StoreCardOpeningHours({ store }: { store: StoreFragment }) {
  const t = useTranslate();
  const { serviceLocale } = useMarket().state.market;
  const openingHours = mapOpeningHours(store, serviceLocale);
  return (
    <ol className="flex flex-col">
      {openingHours.weekly.map((openingHour) => (
        <li key={openingHour.days}>
          {openingHour.days}:{" "}
          {openingHour.closed ? t("closed") : openingHour.hours}
        </li>
      ))}
      {openingHours.special.map((openingHour) => (
        <li key={openingHour.date}>
          {openingHour.label ?? openingHour.date}:{" "}
          {openingHour.closed ? t("closed") : openingHour.hours}
        </li>
      ))}
    </ol>
  );
}

function StoreCardStock({
  store,
  className,
  ...props
}: ComponentProps<"div"> & { store: StoreWithStock }) {
  const t = useTranslate();
  return (
    <div
      className={cn("grid grid-cols-1 items-center gap-2", className)}
      {...props}
    >
      {typeof store.store === "number" && store.store > 0 ? (
        <div className="flex items-center gap-2">
          <StockIndicator
            status={{
              code: StockStatusEnum.IN_STOCK_ITEM,
              quantity: 0,
              pretty: "",
            }}
          />
          <span className="inline-block">{t("availableInStoreStock")}</span>
        </div>
      ) : typeof store.display === "number" && store.display > 0 ? (
        <div className="flex items-center gap-2">
          <StockIndicator
            status={{
              code: StockStatusEnum.IN_STOCK_ITEM,
              quantity: 0,
              pretty: "",
            }}
          />
          <span className="inline-block">{t("availableInShowroom")}</span>
        </div>
      ) : typeof store.storeSiblings === "number" && store.storeSiblings > 0 ? (
        <div className="flex items-center gap-2">
          <StockIndicator
            status={{
              code: StockStatusEnum.IN_STOCK_ITEM,
              quantity: 0,
              pretty: "",
            }}
          />
          <span className="inline-block">
            {t("otherVariantsAvailableInStoreStock")}
          </span>
        </div>
      ) : typeof store.displaySiblings === "number" &&
        store.displaySiblings > 0 ? (
        <div className="flex items-center gap-2">
          <StockIndicator
            status={{
              code: StockStatusEnum.IN_STOCK_ITEM,
              quantity: 0,
              pretty: "",
            }}
          />
          <span className="inline-block">
            {t("otherVariantsAvailableInShowroom")}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <StockIndicator status={{ code: StockStatusEnum.UNKNOWN }} />
          <span className="inline-block">{t("unavailableInStore")}</span>
        </div>
      )}
    </div>
  );
}

// function StoreCardSelector({
//   store,
//   className,
//   ...props
// }: ComponentProps<typeof Button> & { store: StoreWithStock }) {
//   const t = useTranslate();
//   const preferredStoreCode = useStoreLocation((state) => state.storeCode);
//   const setPreferredStoreCode = useStoreLocation((state) => state.setStoreCode);
//   const isPreferredStore = preferredStoreCode === store.storeCode;

//   if (isPreferredStore) {
//     return (
//       <Button
//         size="lg"
//         asChild
//         variant="primary"
//         className={className}
//         {...props}
//       >
//         <span>
//           <Check className="size-4" />
//           {t("yourStore")}
//         </span>
//       </Button>
//     );
//   }

//   return (
//     <Button
//       size="lg"
//       onClick={() => setPreferredStoreCode(store.storeCode ?? "")}
//       className={className}
//       variant="secondary"
//       {...props}
//     >
//       {t("chooseStore")}
//     </Button>
//   );
// }

function StoreCard({ store }: { store: StoreWithStock }) {
  const t = useTranslate();
  const { locale } = useMarket().state.market;

  const mapUrl = new URL("https://www.google.com/maps/search/");
  mapUrl.searchParams.set("api", "1");
  mapUrl.searchParams.set("query", store.title ?? "");

  return (
    <AccordionItem value={store.storeCode ?? ""}>
      <AccordionTrigger className="flex flex-row items-start gap-2 px-6 py-4 hover:cursor-pointer hover:no-underline">
        <div className="flex flex-col items-start gap-1">
          <Heading as="h3" className="col-span-full text-lg">
            {store.address}, {store.city}
          </Heading>
          <StoreCardStock store={store} className="col-span-1" />
        </div>
      </AccordionTrigger>
      <AccordionContent className="flex flex-col gap-4 px-5 text-base">
        {store.storePage?.slug && (
          <Link
            className="all-small-caps text-sm underline underline-offset-4"
            locale={locale}
            href={store.storePage.slug}
          >
            {t("viewStore")}
          </Link>
        )}
        <StoreCardOpeningHours store={store} />
        {/* <StoreCardSelector store={store} className="col-span-full" /> */}
      </AccordionContent>
    </AccordionItem>
  );
}

export default function StoreLocatorList({
  stores,
}: {
  stores: Array<StoreWithStock>;
}) {
  // const preferredStoreCode = useStoreLocation((state) => state.storeCode);

  return (
    <Accordion type="multiple" defaultValue={[]} className="border-b">
      {stores.map((store) => (
        <StoreCard key={store.storeCode ?? store.title} store={store} />
      ))}
    </Accordion>
  );
}
